// Interne Sicht: Eingehende Shop-Bestellungen (für Admin/Innendienst).
// Liste aller Shop-Bestellungen mit Filter, Detail-Modal und
// "→ Auftrag erstellen"-Aktion.
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { ShoppingBag, CheckCircle2, Clock, XCircle, ArrowRight, Eye, RefreshCw, Plus, Trash2, Box } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ManualOrderModal from '../../components/ManualOrderModal';
import { useAuth } from '../../contexts/AuthContext';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(v || 0));

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
  : '–';

const STATUS_META = {
  submitted: { label: 'Eingegangen', color: 'bg-amber-50 text-amber-700', icon: Clock },
  converted: { label: 'In Auftrag', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Storniert', color: 'bg-red-50 text-red-700', icon: XCircle },
  draft: { label: 'Offener Warenkorb', color: 'bg-gray-100 text-gray-500', icon: ShoppingBag },
};

function OrderDetailModal({ order, onClose, onConvert, onCreateDelivery, onCancel, onDelete, busy, isAdmin }) {
  const meta = STATUS_META[order.status] || STATUS_META.submitted;
  const Icon = meta.icon;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <div className="p-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-[#32373c]">Shop-Bestellung #{order.id.slice(0, 8)}</h3>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${meta.color}`}>
                <Icon className="w-3 h-3" /> {meta.label}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              {order.customerName} · {order.customerEmail}
              {order.easybillCustomerId && ` · Easybill #${order.easybillCustomerId}`}
              {order.salePriceLevel && ` · ${order.salePriceLevel}`}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">
              Eingegangen: {fmtDate(order.submittedAt)}
              {order.convertedAt && ` · Konvertiert: ${fmtDate(order.convertedAt)}`}
            </div>
            {order.commission && (
              <div className="mt-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-2">
                <strong>Kommission / Bauvorhaben:</strong> {order.commission}
              </div>
            )}
            {order.deliveryAddressMode === 'custom' && order.deliveryAddress && (
              <div className="mt-1 text-xs text-gray-700 bg-blue-50 border border-blue-200 rounded p-2 whitespace-pre-wrap">
                <strong>Abweichende Lieferadresse:</strong>
                {'\n' + order.deliveryAddress}
              </div>
            )}
            {order.customerNote && (
              <div className="mt-1 text-xs text-gray-700 bg-gray-100 rounded p-2">
                <strong>Mitteilung vom Kunden:</strong> {order.customerNote}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400">
            <XCircle className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-1.5 font-medium">Artikel</th>
                <th className="text-left px-3 py-1.5 font-medium">Gruppe</th>
                <th className="text-right px-3 py-1.5 font-medium">Menge</th>
                <th className="text-right px-3 py-1.5 font-medium">Stückpreis</th>
                <th className="text-right px-3 py-1.5 font-medium">Summe netto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(order.items || []).map((it, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-1.5">
                    <div className="font-medium text-[#32373c]">{it.title}</div>
                    <div className="text-[10px] text-gray-400 font-mono">{it.articleNumber || '–'}</div>
                  </td>
                  <td className="px-3 py-1.5 text-gray-500">{it.groupName || '–'}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{it.quantity} {it.unit}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtEur(it.single_price_net)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmtEur((it.quantity || 0) * (it.single_price_net || 0))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="4" className="text-right px-3 py-2 text-xs text-gray-500">Gesamt netto</td>
                <td className="text-right px-3 py-2 text-sm font-bold text-[#32373c] tabular-nums">{fmtEur(order.totalNet)}</td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right px-3 py-1 text-xs text-gray-500">Gesamt brutto</td>
                <td className="text-right px-3 py-1 text-xs text-gray-600 tabular-nums">{fmtEur(order.totalGross)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Systemprüfungs-Snapshots (eines pro Item, falls vom Kunden im
            Kunden-Dashboard via „Systemprüfung" durchgeführt). Innendienst
            sieht hier die Modul-Daten und die berechnete Trackerbelegung,
            mit denen der Kunde dieses System ausgelegt hat. */}
        {(order.items || []).some(it => it.systemCheck) && (
          <div className="px-4 pb-4 border-t border-gray-100 space-y-3">
            {(order.items || []).filter(it => it.systemCheck).map((it, idx) => (
              <details key={idx} className="bg-[#9ec043]/5 border border-[#9ec043]/30 rounded-lg" data-testid={`internal-syscheck-${idx}`} open>
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-[#7ca735] flex items-center gap-2">
                  <Box className="w-3.5 h-3.5" />
                  Systemprüfung des Kunden — {it.title}
                  {it.systemCheck.summary?.totalPvKw && (
                    <span className="text-gray-500 font-normal">
                      ({it.systemCheck.summary.totalModules} Module, {it.systemCheck.summary.totalPvKw.toFixed(2)} kWp)
                    </span>
                  )}
                </summary>
                <div className="px-3 pb-3 pt-1 text-[11px] text-gray-700 space-y-2">
                  {it.systemCheck.entries?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                        Eingegebene {it.systemCheck.mode === 'strings' ? 'Strings' : 'Modulfelder'}
                      </div>
                      <div className="space-y-1">
                        {it.systemCheck.entries.map((e, j) => (
                          <div key={j} className="bg-white border border-gray-100 rounded px-2 py-1.5">
                            <div className="font-medium text-[#32373c]">
                              {e.name || `Feld ${j + 1}`}
                              {e.moduleType && <span className="text-gray-400 font-normal"> · {e.moduleType}</span>}
                            </div>
                            <div className="text-gray-500 text-[10px] grid grid-cols-2 sm:grid-cols-4 gap-x-3">
                              <span>Module: <strong className="text-gray-700">{e.moduleCount}</strong></span>
                              <span>Voc: <strong className="text-gray-700">{e.vocPerModule} V</strong></span>
                              <span>Wp: <strong className="text-gray-700">{e.powerWpPerModule}</strong></span>
                              <span>Imp: <strong className="text-gray-700">{e.nominalCurrentPerModule} A</strong></span>
                              <span>Ausrichtung: <strong className="text-gray-700">{e.orientation}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {it.systemCheck.assignment?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                        Trackerbelegung (vom System empfohlen)
                      </div>
                      <div className="space-y-1">
                        {it.systemCheck.assignment.map(t => (
                          <div key={t.id} className="bg-white border border-gray-100 rounded px-2 py-1.5 grid grid-cols-[44px_1fr] gap-2">
                            <div className="font-mono text-[#7ca735] font-semibold tabular-nums">{t.id}</div>
                            {(!t.strings || t.strings.length === 0) ? (
                              <span className="text-gray-400 italic">— frei —</span>
                            ) : (
                              <div className="space-y-0.5">
                                {t.strings.map((s, k) => (
                                  <div key={k}>
                                    <strong>{s.name}</strong> · {s.moduleCount} Module · {(s.powerKw || 0).toFixed(2)} kWp
                                    {s.stringVocCold && <> · Voc kalt {s.stringVocCold.toFixed(0)} V</>}
                                    {t.strings.length > 1 && <span className="text-[10px] text-purple-500 ml-1">(parallel)</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        )}

        <div className="p-4 border-t border-gray-100 flex flex-wrap justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100"
          >
            Schließen
          </button>
          {isAdmin && order.status !== 'submitted' && order.status !== 'draft' && (
            <button
              onClick={onDelete}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
              data-testid="btn-delete-shop-order"
            >
              <Trash2 className="w-3.5 h-3.5" /> Bestellung löschen
            </button>
          )}
          {order.status === 'submitted' && (
            <>
              <button
                onClick={onCancel}
                disabled={busy}
                className="px-3 py-1.5 rounded-lg text-xs text-red-600 border border-red-200 hover:bg-red-50 disabled:opacity-50"
                data-testid="btn-cancel-shop-order"
              >
                Stornieren
              </button>
              <button
                onClick={onCreateDelivery}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#32373c] border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                title="Direkt-Lieferschein in Easybill anlegen (für Einzelteile ohne Auftrags-Overhead)"
                data-testid="btn-create-delivery-note"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                {busy ? 'Erstelle …' : 'Lieferschein erstellen'}
              </button>
              <button
                onClick={onConvert}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
                data-testid="btn-convert-to-order"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                {busy ? 'Erstelle …' : 'Auftrag erstellen'}
              </button>
            </>
          )}
          {order.status === 'converted' && order.linkedOrderId && (
            <a
              href={`/dashboard/auftraege?orderId=${order.linkedOrderId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
              data-testid="link-linked-order"
            >
              Zum Auftrag <ArrowRight className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BestellungenPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const url = statusFilter === 'all'
      ? '/api/shop/orders?status='
      : `/api/shop/orders?status=${statusFilter}`;
    const r = await authFetch(url);
    if (r.ok) {
      const d = await r.json();
      setOrders(d.orders || []);
    } else {
      toast.error('Bestellungen konnten nicht geladen werden');
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const convertSelected = async () => {
    if (!selected) return;
    setBusy(true);
    const r = await authFetch(`/api/shop/orders/${selected.id}/convert-to-order`, { method: 'POST' });
    setBusy(false);
    if (r.ok) {
      const d = await r.json();
      toast.success('Auftrag erstellt — Bestellung als „In Bearbeitung" markiert.');
      setSelected(null);
      await load();
      if (window.confirm('Direkt zum neuen Auftrag wechseln?')) {
        navigate(`/dashboard/auftraege?orderId=${d.linkedOrderId}`);
      }
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Konvertierung fehlgeschlagen');
    }
  };

  // 28.02.2026 Punkt-3-Runde-1: Direkter Lieferschein-Flow für einzelnes
  // Zubehör (z. B. ET340) — legt einen Easybill-DELIVERY-Beleg an und
  // erzeugt einen Order-Datensatz mit `orderKind='bestellung'`.
  const createDeliveryForSelected = async () => {
    if (!selected) return;
    if (!selected.easybillCustomerId) {
      toast.error('Bestellung hat keine Easybill-Kunden-ID — bitte zuerst verknüpfen.');
      return;
    }
    if (!window.confirm(
      'Lieferschein direkt in Easybill anlegen?\n\n' +
      'Diese Bestellung wird OHNE Begleitdokument und OHNE Produktions-Workflow ' +
      'verarbeitet — ideal für einzelnes Zubehör (z. B. Energy-Meter). ' +
      'Rechnung erstellst du später direkt aus dem Lieferschein in Easybill.'
    )) return;
    setBusy(true);
    const r = await authFetch(`/api/shop/orders/${selected.id}/create-delivery-note`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    setBusy(false);
    if (r.ok) {
      const d = await r.json();
      toast.success(
        d.easybillDocumentNumber
          ? `Lieferschein ${d.easybillDocumentNumber} in Easybill angelegt.`
          : 'Lieferschein in Easybill angelegt.'
      );
      setSelected(null);
      await load();
      if (window.confirm('Direkt zur Bestellung wechseln?')) {
        navigate(`/dashboard/auftraege?orderId=${d.linkedOrderId}`);
      }
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Lieferschein-Anlage fehlgeschlagen');
    }
  };

  const cancelSelected = async () => {
    if (!selected) return;
    if (!window.confirm('Bestellung wirklich stornieren?')) return;
    setBusy(true);
    const r = await authFetch(`/api/shop/orders/${selected.id}/cancel`, { method: 'POST' });
    setBusy(false);
    if (r.ok) {
      toast.success('Bestellung storniert');
      setSelected(null);
      await load();
    } else {
      toast.error('Stornierung fehlgeschlagen');
    }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Shop-Bestellung #${selected.id.slice(0, 8)} unwiderruflich löschen?\n\nDer ggf. bereits erzeugte Auftrag bleibt davon unberührt.`,
    )) return;
    setBusy(true);
    const r = await authFetch(`/api/shop/orders/${selected.id}`, { method: 'DELETE' });
    setBusy(false);
    if (r.ok) {
      toast.success('Bestellung gelöscht');
      setSelected(null);
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Löschen fehlgeschlagen');
    }
  };

  const counts = {
    submitted: orders.filter(o => o.status === 'submitted').length,
    converted: orders.filter(o => o.status === 'converted').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="bestellungen-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-[#9ec043]" />
          <h1 className="text-2xl font-semibold text-[#32373c]">Eingehende Bestellungen</h1>
          <span className="text-xs text-gray-400">({orders.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowManual(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735]"
            data-testid="btn-new-manual-order"
          >
            <Plus className="w-3.5 h-3.5" /> Manuelle Bestellung
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs hover:bg-gray-200"
            data-testid="btn-refresh-bestellungen"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Status-Filter */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[
          { key: 'submitted', label: 'Eingegangen', icon: Clock },
          { key: 'converted', label: 'In Auftrag', icon: CheckCircle2 },
          { key: 'cancelled', label: 'Storniert', icon: XCircle },
          { key: 'all', label: 'Alle', icon: null },
        ].map(f => {
          const Icon = f.icon;
          const active = statusFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                active ? 'bg-[#9ec043] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Tabelle */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Lade …</div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400" data-testid="empty-orders">
            Keine Bestellungen in dieser Ansicht.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Bestellung</th>
                <th className="text-left px-3 py-2 font-medium">Kunde</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Artikel</th>
                <th className="text-right px-3 py-2 font-medium">Summe netto</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map(o => {
                const meta = STATUS_META[o.status] || STATUS_META.submitted;
                const Icon = meta.icon;
                return (
                  <tr key={o.id} data-testid={`order-row-${o.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono text-[11px] text-gray-500">#{o.id.slice(0, 8)}</div>
                      <div className="text-[10px] text-gray-400">{fmtDate(o.submittedAt || o.createdAt)}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#32373c]">{o.customerName || '–'}</div>
                      <div className="text-[10px] text-gray-400">{o.customerEmail}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{(o.items || []).length}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-[#32373c]">{fmtEur(o.totalNet)}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setSelected(o)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-gray-500 hover:bg-gray-100"
                        data-testid={`btn-view-${o.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <OrderDetailModal
          order={selected}
          busy={busy}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onConvert={convertSelected}
          onCreateDelivery={createDeliveryForSelected}
          onCancel={cancelSelected}
          onDelete={deleteSelected}
        />
      )}
      {showManual && (
        <ManualOrderModal
          onClose={() => setShowManual(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
}
